import { stripHtmlTags } from '@chiroup/core/functions/stripHtmlTags';
import { Heart as HeartIcon } from 'lucide-react';

import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import React from 'react';
import { Link } from 'react-router-dom';
import { hcVerbiage } from '../../common/verbiage';
import { usePatientPortal } from '../../contexts/PatientPortalContext';
import FallbackImage from '../common/FallbackImage';

const S3_ASSETS_URL = import.meta.env.VITE_S3_ASSETS_URL as string;

const Conditions: React.FC<{ basePath: string }> = ({ basePath }) => {
  const { selectedPatient, language } = usePatientPortal();
  const primaryColor = selectedPatient?.clinic?.primaryColor || '#77bd43';

  if (!selectedPatient?.conditions?.length) {
    return (
      <div className="text-center py-16">
        <p className="text-sm text-gray-400 dark:text-gray-500">
          {hcVerbiage.noConditions[language]}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          {hcVerbiage.conditions[language]}
        </h2>
        <p className="mt-1 text-sm text-gray-400 dark:text-gray-500">
          {hcVerbiage.conditionsDescription[language]}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {selectedPatient.conditions.map((condition: any) => {
          const imgSrc = condition.commonImg
            ? condition.commonImg.startsWith('http')
              ? condition.commonImg
              : `${S3_ASSETS_URL}/${condition.commonImg}`
            : null;
          const description = stripHtmlTags(condition.descr);

          return (
            <Link
              key={condition.ID}
              to={`${basePath}/conditions/${condition.ID}`}
              className="portal-card flex items-center gap-3 p-3.5 active:scale-[0.98] transition-transform"
            >
              <FallbackImage
                src={imgSrc}
                alt={condition.commonName}
                imgClassName="shrink-0 w-12 h-12 rounded-xl object-cover"
                fallback={
                  <div
                    className="shrink-0 w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${primaryColor}15` }}
                  >
                    <HeartIcon className="h-5 w-5" style={{ color: primaryColor }} />
                  </div>
                }
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {condition.commonName}
                </p>
                {!!description && (
                  <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500 line-clamp-1">
                    {description}
                  </p>
                )}
              </div>
              <Icon
                icon={Icons.chevronRight}
                className="h-4 w-4 text-gray-300 dark:text-gray-600 shrink-0"
              />
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default Conditions;
